<template>
	<div
		class="drawer"
		:class="[
			{
				'--open': isOpen,
				'--small': small,
				'--border-sandwich': borderSandwich
			},
			bgColor ? `bg--${bgColor}` : ''
		]"
	>
		<slot name="before-title" />
		<h3
			:class="[
				small ? 'h6' : '',
				'drawer-title',
				bgColor ? `bg--${bgColor}` : ''
			]"
		>
			<button
				:aria-expanded="isOpen ? 'true' : 'false'"
				type="button"
				class="button--unstyled"
				@click="toggleDrawer"
				:id="`drawerTrigger${drawerId}`"
				:aria-controls="`drawerContent${drawerId}`"
			>
				<slot name="title">Drawer title</slot>
				<span class="icon icon-down">
					<span class="visually-hidden">{{ buttonIconText }}</span>
				</span>
			</button>
		</h3>
		<transition-expand>
			<div
				v-show="isOpen"
				class="drawer-content"
				:id="`drawerContent${drawerId}`"
				:aria-labelledby="`drawerTrigger${drawerId}`"
				role="region"
			>
				<slot name="content">Drawer contents go here</slot>
			</div>
		</transition-expand>
	</div>
</template>

<script>
import TransitionExpand from "../transitions/TransitionExpand.vue";
import { UniqueId } from "../../../utilities";

export default {
	name: "Drawer",
	components: { TransitionExpand },
	props: {
		startOpen: {
			default: false
		},
		borderSandwich: {
			default: false
		},
		bgColor: {
			type: String
		},
		small: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			isOpen: this.startOpen,
			drawerId: new UniqueId("-").generate()
		};
	},
	computed: {
		buttonIconText() {
			return this.isOpen ? "Collapse Icon" : "Expand Icon";
		}
	},
	methods: {
		toggleDrawer() {
			this.isOpen = !this.isOpen;
		},
		forceOpen() {
			this.isOpen = true;
		}
	}
};
</script>

<style lang="scss">
// Clickable title
.drawer {
	&.\--border-sandwich {
		border-bottom: 2px solid $color--lighter-gray;
		border-top: 2px solid $color--lighter-gray;
		padding-top: 26px;

		.drawer-title {
			border-bottom: none;
			margin-bottom: 0;
		}
	}

	.h6.drawer-title {
		font-weight: $font-weight--bold;
		margin-bottom: 0;

		button {
			padding-bottom: 18px;
			padding-top: 20px;
		}

		.icon {
			color: $color--red;
			top: calc(50% - 2px);
		}
	}
}

.drawer-content {
	overflow: hidden;
	.\--open & {
		overflow: visible; //Show overflow when the drawer is open. Necessary when tooltips are used, which flow outside the drawer
		margin-bottom: 40px;
		&.expand-enter-active {
			overflow: hidden !important; //Override in-line styles that are in the Expand transition component
		}
	}
	.\--open.\--small & {
		margin-bottom: 0;
	}
}

.drawer-title {
	border-bottom: 2px solid $color--lighter-gray;

	button {
		cursor: pointer;
		padding-bottom: 24px;
		padding-right: 40px; // avoid collision with the chevron icon
		position: relative;
		text-align: left;
		width: 100%;

		.vg-is-tabbing &:focus {
			outline: none; // We'll defer the focus outline to the icon for aesthetics

			.icon {
				outline: 1px dotted #212121;
				outline: 5px auto -webkit-focus-ring-color;
			}
		}

		&:active {
			color: $color--almost-black;
		}
	}

	.icon {
		display: table;
		font-size: 14px;
		pointer-events: none; // simplifies click handling
		position: absolute;
		right: 0;
		top: calc(50% - 12px);
		transform: translateY(-50%) rotateX(0deg);
		transform-origin: center;
		transition: 0.25s transform;
	}
}

.\--open .drawer-title .icon {
	transform: translateY(-50%) rotateX(180deg);
}
</style>
